import { Link } from "react-router-dom";
import { BookOpen, ArrowRight } from "lucide-react";
import { SectionHeading } from "../components/PageBits";
import { Reveal, RevealLines } from "../components/Reveal";

// Cuando lleguen los libros oficiales, añade objetos aquí:
// { cover, title, description, audience, format, href, status: "Disponible" | "Próximamente" }
const BOOKS = [];

const scrollToCollection = () =>
  document.getElementById("coleccion")?.scrollIntoView({ behavior: "smooth" });

export default function Books() {
  return (
    <>
      {/* 1. Hero */}
      <section className="grain relative overflow-hidden bg-navy pt-40 pb-16 sm:pt-48 sm:pb-24" data-testid="books-hero">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[150px]" />
        </div>
        <div className="container-luxe relative z-10">
          <Reveal>
            <p className="overline mb-7 flex items-center gap-4">
              <span className="h-px w-10 bg-gold" /> Libros
            </p>
          </Reveal>
          <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.04] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            <RevealLines lines={["Ideas que encontraron"]} delay={0.2} />
            <RevealLines lines={["su propia voz."]} lineClassName="italic text-gold" delay={0.32} />
          </h1>
          <div className="mt-8 max-w-2xl space-y-5">
            <Reveal delay={0.5}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                Antes de que existiera un método para enseñar, hubo ideas que necesitaban ser escritas.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                Hoy esas ideas toman la forma de libros que acompañan, profundizan y enriquecen el
                recorrido de la Academia Desde el Primer Latido™.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. ¿Por qué existen estos libros? */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="books-why">
        <div className="container-luxe">
          <SectionHeading
            dark={false}
            overline="Más que un complemento"
            title="Cada libro nace de"
            accent="una necesidad distinta."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-7">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-navy/80">Los programas enseñan.</p>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  La Biblioteca organiza un recorrido.
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  Los libros permiten detenerse, reflexionar y explorar una idea con mayor profundidad.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-navy">
                No repiten el contenido de la Academia.
                <br />
                <span className="italic text-gold-deep">Lo amplían desde otra perspectiva.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. Colección de libros */}
      <section id="coleccion" className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="books-collection">
        <div className="container-luxe relative z-10">
          <SectionHeading overline="Colección en desarrollo" title="Nuevas historias están" accent="tomando forma." />

          {BOOKS.length === 0 ? (
            <Reveal>
              <div className="flex flex-col items-center gap-6 rounded-sm border border-gold/20 bg-navy-soft/30 px-8 py-20 text-center backdrop-blur-sm">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 text-gold">
                  <BookOpen size={26} />
                </span>
                <p className="max-w-xl text-base font-light leading-relaxed text-cream/75">
                  Estamos preparando una colección de libros para niños, familias y adultos, creada
                  para acompañar diferentes etapas del crecimiento emocional y consciente.
                </p>
                <p className="max-w-xl text-base font-light leading-relaxed text-cream/75">
                  Muy pronto compartiremos aquí los primeros títulos oficiales.
                </p>
                <span className="mt-2 rounded-full border border-gold/30 px-4 py-1.5 text-[0.6rem] uppercase tracking-luxe text-gold">
                  Próximamente
                </span>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {BOOKS.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.08}>
                  <article className="group flex h-full flex-col" data-testid={`book-card-${i}`}>
                    <div className="relative aspect-[3/4] overflow-hidden rounded-sm border border-gold/20">
                      {b.cover ? (
                        <img src={b.cover} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-navy-soft/40">
                          <BookOpen size={30} className="text-gold/30" />
                        </div>
                      )}
                      <span className="absolute left-4 top-4 rounded-full border border-gold/40 bg-navy/70 px-3 py-1 text-[0.55rem] uppercase tracking-luxe text-gold backdrop-blur-sm">
                        {b.status}
                      </span>
                    </div>
                    <h3 className="mt-5 font-serif text-2xl leading-tight text-cream">{b.title}</h3>
                    <p className="mt-3 text-sm font-light leading-relaxed text-cream/70">{b.description}</p>
                    {(b.audience || b.format) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {b.audience && (
                          <span className="rounded-full border border-gold/25 px-3 py-1 text-[0.65rem] font-light text-cream/80">{b.audience}</span>
                        )}
                        {b.format && (
                          <span className="rounded-full border border-gold/25 px-3 py-1 text-[0.65rem] font-light text-cream/80">{b.format}</span>
                        )}
                      </div>
                    )}
                    <a
                      href={b.href || "#"}
                      className="mt-5 inline-flex flex-1 items-end gap-2 text-sm text-gold transition-transform duration-300 hover:translate-x-1"
                    >
                      {b.status === "Disponible" ? "Conseguir mi ejemplar" : "Leer más"} <ArrowRight size={14} />
                    </a>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 4. Relación con la Academia */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="books-relation">
        <div className="container-luxe">
          <SectionHeading dark={false} overline="Un mismo ecosistema" title="Una misma visión," accent="distintos formatos." />
          <div className="max-w-3xl space-y-6">
            <Reveal>
              <p className="text-lg font-light leading-relaxed text-navy/80">
                Los libros, la Biblioteca Premium y los Programas forman parte de un mismo ecosistema de
                aprendizaje.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-navy/80">
                Cada formato cumple una función diferente, pero todos comparten una misma intención:
                ayudarte a comprender tu historia, desarrollar mayor conciencia y transformar la forma en
                que vives.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5. Cierre */}
      <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="books-closing">
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-snug text-cream sm:text-3xl">
              Algunas ideas necesitan ser estudiadas.
              <br />
              Otras necesitan ser vividas.
              <br />
              <span className="italic text-gold">Y algunas merecen ser leídas con calma.</span>
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 text-base font-light leading-relaxed text-cream/75 sm:text-lg">
              Los libros nacen para acompañarte precisamente en ese espacio.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <button onClick={scrollToCollection} className="btn-gold mt-10" data-testid="books-cta">
              Explorar la colección <ArrowRight size={15} />
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
